import { Modal, Form, Input, Button, message, Row, Col } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, TeamOutlined } from '@ant-design/icons';
import { useAuthStore } from '../stores/auth';
import { api } from '../utils/request';
import { useTranslation } from 'react-i18next';
import { Select } from 'antd';

const USER_ROLE_OPTIONS = [
  { value: 'personal_developer', labelKey: 'auth.register.rolePersonalDeveloper' },
  { value: 'sales', labelKey: 'auth.register.roleSales' },
  { value: 'corporate_employee', labelKey: 'auth.register.roleCorporateEmployee' },
  { value: 'educator', labelKey: 'auth.register.roleEducator' },
  { value: 'other', labelKey: 'auth.register.roleOther' },
];

interface ProfileModalProps {
  visible: boolean;
  onClose: () => void;
}

export default function ProfileModal({ visible, onClose }: ProfileModalProps) {
  const [form] = Form.useForm();
  const { user, updateUser } = useAuthStore();
  const { t } = useTranslation();

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload: any = {};
      Object.keys(values).forEach((key) => {
        if (values[key] !== undefined) {
          payload[key] = values[key];
        }
      });
      await api.put('/users/profile', payload);
      updateUser(payload);
      message.success(t('profile.saveSuccess'));
      onClose();
    } catch {
      message.error(t('profile.saveSuccess'));
    }
  };

  return (
    <Modal
      title={t('profile.profileInfo')}
      open={visible}
      onOk={handleOk}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('dialog.cancel')}
        </Button>,
        <Button key="submit" type="primary" onClick={handleOk}>
          {t('profile.saveChanges')}
        </Button>,
      ]}
      width={560}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          username: user?.username,
          realName: user?.realName,
          email: user?.email,
          phone: user?.phone,
          companyName: user?.companyName,
          userRole: user?.userRole,
        }}
        className="mt-4"
      >
        <Form.Item label={t('profile.username')} name="username">
          <Input prefix={<UserOutlined />} disabled />
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label={t('profile.realName')} name="realName" rules={[{ required: true, message: t('auth.register.usernameRequired') }]}>
              <Input prefix={<UserOutlined />} placeholder={t('auth.register.usernamePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('profile.email')} name="email" rules={[{ type: 'email', message: t('auth.register.emailInvalid') }]}>
              <Input prefix={<MailOutlined />} placeholder={t('auth.register.emailPlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label={t('profile.phone')} name="phone">
              <Input prefix={<PhoneOutlined />} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('profile.companyName')} name="companyName">
              <Input prefix={<TeamOutlined />} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label={t('profile.userRole')} name="userRole">
          <Select allowClear placeholder={t('auth.register.rolePlaceholder')}>
            {USER_ROLE_OPTIONS.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
}
